import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TriangleIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { CRESTMONT, CrestmontHeader } from '../components/crestmont/CrestmontChrome';
import { Footer } from '../components/Footer';
import { CrestmontLeadForm } from '../components/crestmont/CrestmontLeadForm';
import { BenefitsSection, CtaBand, FaqSection } from '../components/crestmont/CrestmontDetails';
import { crestmontPrograms } from '../data/crestmontPrograms';
import { getUniversityBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';

// Institution-level Crestmont University page, built to the same brand guide
// as the degree pages. Marketing copy comes from the existing Crestmont
// advertiser record so the two stay in sync; the degree cards deep-link into
// the BBA and AA-BA pages.
export function CrestmontUniversityPage() {
  const navigate = useNavigate();
  const university = getUniversityBySlug('crestmont-university');
  const content = university?.landingContent;
  // The site footer navigates by page key, matching the rest of the site.
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  useMetaTags({
    title: 'Crestmont University — Online Degrees Built Around Your Life',
    description: content?.subheadline || 'Accredited online degree programs from Crestmont University.',
    robots: 'noindex, follow'
  });
  const scrollToForm = () => {
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const degrees = Object.values(crestmontPrograms);
  const programOptions = degrees.map(entry => ({
    label: entry.name,
    value: entry.mProgramValue
  }));
  return <div className="min-h-screen bg-white w-full" data-id="element-1416" data-mp="3etdutpldbcrl">
      <CrestmontHeader onApply={scrollToForm} data-id="element-1417" data-mp="3js8bolzryr92" />

      <main data-id="element-1418" data-mp="3jqx7il2f3hhf">
        {/* Hero */}
        <section className="py-14 md:py-20" style={{
        backgroundColor: CRESTMONT.navy
      }} data-id="element-1419" data-mp="1pb8bt3y4dmsp">
          <div className="max-w-6xl mx-auto px-4 grid gap-10 lg:grid-cols-5 lg:items-start" data-id="element-1420" data-mp="1kl0852v4kqdb">
            <div className="lg:col-span-3" data-id="element-1421" data-mp="1giwxggga5pt3">
              
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-[1.05] tracking-tight max-w-lg" data-id="element-1422" data-mp="1gjep0zrzzfuo">
                Education tailored to your life.
              </h1>
              <p className="mt-5 text-sm md:text-base text-white/75 max-w-lg leading-relaxed" data-id="element-1423" data-mp="289symjsza60j">
                {content?.subheadline}
              </p>
              <ul className="mt-8 space-y-2.5 max-w-lg" data-id="element-1424" data-mp="1z9eazdsjketv">
                {(content?.heroHighlights || []).map(highlight => <li key={highlight} className="flex items-start gap-2.5" data-id="element-1425" data-mp="1ub30r1yc42b4">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.gold
                }} aria-hidden="true" data-id="element-1426" data-mp="nuhks0kmfjkx" />
                    <span className="text-sm leading-relaxed text-white/80" data-id="element-1427" data-mp="ittk0n6lbodi">
                      {highlight}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-2" data-id="element-1428" data-mp="drc6d0d46y52">
              <CrestmontLeadForm programValue={degrees[0].mProgramValue} programName="program you choose" programOptions={programOptions} heading="Request Program Information" subheading="Tell us where to reach you and a Crestmont advisor will follow up with program details, transfer credit, and next steps." data-id="element-1429" data-mp="ipnkvoectwtm" />
            </div>
          </div>
        </section>

        {/* Stats strip */}
        {content?.stats && content.stats.length > 0 && <section className="border-b border-gray-200" style={{
        backgroundColor: CRESTMONT.mist
      }} aria-label="Crestmont at a glance" data-id="element-1430" data-mp="11h6kdl966sqw">
            <dl className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1431" data-mp="16cvmnwckqnqw">
              {content.stats.map(stat => <div key={stat.label} className="text-center" data-id="element-1432" data-mp="386bid7niqssq">
                  <dt className="sr-only" data-id="element-1433" data-mp="388p8r3797gty">{stat.label}</dt>
                  <dd data-id="element-1434" data-mp="12h9f9g29j31q">
                    <span className="block text-2xl font-extrabold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1435" data-mp="12grcs1p0bsld">
                      {stat.value}
                    </span>
                    <span className="mt-1 block text-[11px] text-gray-500" data-id="element-1436" data-mp="1bih1v0vf2ffz">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </section>}

        {/* Benefits + accreditation — placed under the hero so the credibility
            signals land before the longer institutional detail. */}
        <BenefitsSection benefits={degrees[0].benefits} data-id="element-1437" data-mp="skehizdtd6oi" />

        {/* Degree programs — the primary decision on this page */}
        <section className="py-16 md:py-20 bg-white" aria-labelledby="degrees-heading" data-id="element-1438" data-mp="nsmrqr2hf4zf">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1439" data-mp="2sg2ctwfmgr">
            <h2 id="degrees-heading" className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1440" data-mp="3ra4w2oj1ptv3">
              Choose your business degree.
            </h2>
            <p className="mt-3 text-sm text-gray-600 max-w-xl" data-id="element-1441" data-mp="2biklei19wmsb">
              Both degrees run one course at a time in five-week terms. Every
              associate credit transfers into the bachelor’s, so starting
              smaller costs you nothing.
            </p>

            <ul className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-1442" data-mp="22o4pi9sm3s9y">
              {degrees.map(degree => <li key={degree.slug} className="flex" data-id="element-1443" data-mp="2qdz2qsgufiuq">
                  <a href={`/universities/crestmont-university/${degree.slug}`} className="group flex flex-col w-full border border-gray-200 rounded-lg p-6 transition-colors hover:border-[#00426b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b]" data-id="element-1444" data-mp="2uhydeuffzng6">
                    <span className="inline-flex self-start px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide" style={{
                  backgroundColor: CRESTMONT.mist,
                  color: CRESTMONT.navy
                }} data-id="element-1445" data-mp="152qojrzz09bi">
                      {degree.degreeLevel}
                    </span>
                    <h3 className="mt-4 text-lg font-bold leading-snug" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1446" data-mp="lxlua2t6qpua">
                      {degree.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1447" data-mp="c8u2buo4k5jd">
                      {degree.heroSubhead}
                    </p>
                    <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2" data-id="element-1448" data-mp="7aivztx1gd5w">
                      <div data-id="element-1449" data-mp="29d0jobzubso">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1450" data-mp="2c8znfzkv6ip">
                          Credits
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1451" data-mp="3pfm6ju5rxhdp">
                          {degree.curriculum.totalCredits}
                        </dd>
                      </div>
                      <div data-id="element-1452" data-mp="1v705fb3rfhm4">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1453" data-mp="1v7s26p93fldz">
                          Standard plan
                        </dt>
                        <dd className="text-sm font-bold" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1454" data-mp="203qkpw7qq96h">
                          {degree.stats[0].value}
                        </dd>
                      </div>
                      <div data-id="element-1455" data-mp="1c8xn1f5pwixa">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1456" data-mp="1c9p6jrm1iq77">
                          Total tuition
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1457" data-mp="1m5sifp6p237c">
                          {degree.requirements.tuition.total}
                        </dd>
                      </div>
                    </dl>
                    <span className="mt-auto pt-5 border-t border-gray-100 inline-flex items-center gap-1.5 text-sm font-bold" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1458" data-mp="31mspy7nysx9h">
                      Explore the {degree.shortName}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1459" data-mp="3ir2u6vge70o4" />
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Why Crestmont */}
        {content?.features && content.features.length > 0 && <section className="py-16 md:py-20" style={{
        backgroundColor: CRESTMONT.mist
      }} aria-labelledby="why-heading" data-id="element-1460" data-mp="5avcniivnfij">
            <div className="max-w-5xl mx-auto px-4" data-id="element-1461" data-mp="k6ktz3d4b4gx">
              <h2 id="why-heading" className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1462" data-mp="f8syy0h6f28i">
                Why students choose Crestmont
              </h2>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-1463" data-mp="2h0pfjoi7b2a5">
                {content.features.map(feature => <li key={feature.title} className="bg-white rounded-lg p-6 border-l-2" style={{
              borderLeftColor: CRESTMONT.gold
            }} data-id="element-1464" data-mp="whllciqps0ki">
                    <h3 className="text-sm font-bold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1465" data-mp="11jazi798guct">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1466" data-mp="rpkbf3h0qlxn">
                      {feature.description}
                    </p>
                  </li>)}
              </ul>
            </div>
          </section>}

        {/* Long-form institutional content */}
        {content?.infoSections && content.infoSections.length > 0 && <section className="py-16 md:py-20 bg-white" data-id="element-1467" data-mp="8sj5ngfn4hsn">
            <div className="max-w-3xl mx-auto px-4 space-y-12" data-id="element-1468" data-mp="8t2mettk1053">
              {content.infoSections.map(section => <article key={section.heading} data-id="element-1469" data-mp="1y80d3ldtv0sz">
                  {section.eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-1470" data-mp="236tv9r427fzh">
                      {section.eyebrow}
                    </p>}
                  <h2 className="mt-2 text-xl md:text-2xl font-extrabold tracking-tight" style={{
              color: CRESTMONT.navy
            }} data-id="element-1471" data-mp="2kc9azsjw0lmo">
                    {section.heading}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600" data-id="element-1472" data-mp="2bjpkjfdr1ncx">
                    {section.body}
                  </p>
                  {section.bullets && <ul className="mt-4 space-y-2" data-id="element-1473" data-mp="2zabhobm7tn85">
                      {section.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1474" data-mp="339u45btoq6d3">
                          <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1475" data-mp="2pc5g5v4bmj3t" />
                          <span className="text-sm leading-relaxed text-gray-700" data-id="element-1476" data-mp="1dw73lh9xpexu">
                            {bullet}
                          </span>
                        </li>)}
                    </ul>}
                </article>)}
            </div>
          </section>}

        <CtaBand heading={content?.finalCta?.headline || 'Interested in learning more about our degrees?'} body={content?.finalCta?.body || ''} onApply={scrollToForm} data-id="element-1477" data-mp="1b9xypuimqaxs" />
        <FaqSection faq={degrees[0].faq} data-id="element-1478" data-mp="1kbeaa5l6kj3l" />
      </main>

      <Footer onNavigate={handleNavigate} data-id="element-1479" data-mp="1ka36i5rqjbch" />
    </div>;
}